import {
  IDeliveryTicketResponse,
  IDepartmentOperationProfileResponse,
  IOperationEventResponse,
  IOperationLiveTaskResponse,
  IOperationRequestResponse,
  IOperationTriggerResponse,
  IOperationsEnvironResponse,
  ITimelineSegmentResponse,
} from 'types/api/operationsAimApi.interface'
import { IOpsTumSegment, OpsLossBucket } from 'types/opsAnalytics.interface'
import { IHandoverReportResponse } from 'types/api/operationsHandoverApi.interface'
import { IOpsLiveTask, IOpsEvent, IOpsTriggerRow, IOpsHandoverReport, OpsAlertSeverity } from 'types/operationsControl.interface'
import { IOpsRequest } from 'types/opsRequests.interface'
import { IOpsDepartment } from 'types/opsDepartments.interface'
import { IOpsBlastWindow, IOpsEnviron, IOpsWeather } from 'types/opsToday.interface'
import { formatDateForDisplay } from 'utils/helpers'
import {
  BLAST_STATE_TO_UI,
  EVENT_KIND_TO_UI,
  EVENT_SEVERITY_TO_UI,
  KPI_TEMPLATE_TO_UI,
  LOSS_BUCKET_TO_UI,
  REQUEST_PRIORITY_TO_UI,
  REQUEST_SLA_TO_UI,
  REQUEST_STATUS_TO_UI,
  TASK_SOURCE_TO_UI,
  TRIGGER_MODE_TO_UI,
  TRIGGER_PRIORITY_TO_UI,
  UNASSIGNED_REASON_LABEL,
} from './opsEnumMaps'

const DAY_STRIP_START_HOUR = 6

const hhmm = (d: Date) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`

export const mapTimelineSegments = (segments: ITimelineSegmentResponse[]): IOpsTumSegment[] =>
  segments
    .map((seg, index) => {
      const from = new Date(seg.from)
      const to = new Date(seg.to)
      if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null
      const dayStart = new Date(from.getFullYear(), from.getMonth(), from.getDate(), DAY_STRIP_START_HOUR, 0, 0)
      const fromMin = Math.round((from.getTime() - dayStart.getTime()) / 60000)
      const toMin = Math.round((to.getTime() - dayStart.getTime()) / 60000)
      return {
        id: index + 1,
        fromMin,
        toMin,
        bucket: (LOSS_BUCKET_TO_UI[seg.bucket] ?? 'NO_TASK') as OpsLossBucket,
        activity: null,
        taskName: null,
        taskId: null,
        location: `${hhmm(from)} – ${hhmm(to)}`,
        note: seg.note ?? null,
      }
    })
    .filter(Boolean) as IOpsTumSegment[]

export const neededByToDisplay = (neededBy: string): string => {
  if (!neededBy) return '—'
  if (neededBy.includes('T')) {
    const d = new Date(neededBy)
    if (!Number.isNaN(d.getTime())) {
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    }
  }
  return neededBy
}

export const neededByTimeToIso = (hhmm: string): string => {
  const [h, m] = hhmm.split(':').map(Number)
  const d = new Date()
  d.setHours(h, m, 0, 0)
  return d.toISOString()
}

export const deptKeyByHrId = (profiles: IOpsDepartment[], hrDepartmentId: number): string => {
  const match = profiles.find((p) => p.hrDepartmentId === hrDepartmentId)
  return match?.key ?? `dept-${hrDepartmentId}`
}

export const deptHrIdByKey = (profiles: IOpsDepartment[], key: string): number | null => {
  const match = profiles.find((p) => p.key === key)
  return match?.hrDepartmentId ?? null
}

export const mapLiveTask = (row: IOperationLiveTaskResponse): IOpsLiveTask => ({
  id: row.id,
  name: row.name,
  source: TASK_SOURCE_TO_UI[row.source] ?? 'MANUAL',
  sourceId: row.sourceId,
  sourceName: row.sourceName,
  planId: row.planId,
  department: row.departmentKey ?? '',
  machine: row.machineName,
  operator: row.operatorName,
  taskStatus: row.taskStatus,
  etaMin: row.etaMin,
  location: row.locationName ?? '—',
  blockedReason: row.blockedReason,
  unassignedReason: row.unassignedReason != null ? UNASSIGNED_REASON_LABEL[row.unassignedReason] ?? null : null,
  generatedAt: row.generatedAt ?? '',
})

export const mapEvent = (row: IOperationEventResponse): IOpsEvent => ({
  id: row.id,
  time: formatDateForDisplay(row.occurredAt, true),
  kind: EVENT_KIND_TO_UI[row.kind] ?? 'alert_raised',
  severity: EVENT_SEVERITY_TO_UI[row.severity] ?? 'info',
  title: row.title,
  description: row.description ?? '',
  department: row.departmentKey,
  deptTo: row.deptToKey,
  source: row.source != null ? TASK_SOURCE_TO_UI[row.source] ?? null : null,
  taskId: row.taskId,
  planId: row.planId,
  requestId: row.requestId,
  linkPath: row.taskId
    ? `/operations/task/${row.taskId}`
    : row.planId
      ? `/operations/plans/${row.planId}`
      : row.requestId
        ? '/operations/today'
        : null,
})

export const mapRequest = (row: IOperationRequestResponse, profiles: IOpsDepartment[]): IOpsRequest => ({
  id: row.id,
  code: row.code ?? `REQ-${row.id}`,
  fromDept: deptKeyByHrId(profiles, row.fromDeptId),
  toDept: deptKeyByHrId(profiles, row.toDeptId),
  title: row.title,
  description: row.description ?? '',
  linkedEntityLabel: null,
  linkedPath: row.linkedEntityId
    ? row.linkedEntityType === 1
      ? `/operations/task/${row.linkedEntityId}`
      : row.linkedEntityType === 2
        ? `/operations/plans/${row.linkedEntityId}`
        : null
    : null,
  priority: REQUEST_PRIORITY_TO_UI[row.priority] ?? 'normal',
  neededBy: row.neededBy,
  status: REQUEST_STATUS_TO_UI[row.status] ?? 'open',
  declineReason: row.declineReason,
  createdBy: row.fromDeptName ? `User (${row.fromDeptName})` : '—',
  createdAt: formatDateForDisplay(row.createdAt, true),
  acknowledgedAt: row.acknowledgedAt,
  escalationLevel: row.escalationLevel,
  slaState: REQUEST_SLA_TO_UI[row.slaState],
})

export const mapDepartmentProfile = (row: IDepartmentOperationProfileResponse): IOpsDepartment => ({
  key: row.key,
  hrDepartmentId: row.hrDepartmentId,
  name: row.name,
  kpiTemplate: KPI_TEMPLATE_TO_UI[row.kpiTemplate] ?? 'custom',
  icon: row.icon ?? 'clipboard',
  sortOrder: row.sortOrder,
  opsEnabled: row.operationEnabled,
})

export const mapTrigger = (row: IOperationTriggerResponse): IOpsTriggerRow => ({
  id: row.id,
  mode: TRIGGER_MODE_TO_UI[row.triggerMode] ?? 'COMPLETION',
  name: row.name,
  scope: row.scope ?? '—',
  condition: row.condition,
  action: row.action ?? '—',
  actionTarget: '—',
  priority: (TRIGGER_PRIORITY_TO_UI[row.priority] ?? 'normal') as IOpsTriggerRow['priority'],
  fireCount: row.fireCount,
  lastFired: row.lastFired,
  nextExpected: row.nextExpected,
  enabled: row.enabled,
  schedule: null,
  threshold: null,
})

export const mapEnviron = (
  row: IOperationsEnvironResponse,
  zoneNames: { [zoneId: number]: string },
): IOpsEnviron => {
  const blastWindows: IOpsBlastWindow[] = row.blastWindows.map((b) => ({
    id: b.id,
    zone: zoneNames[b.zoneId] ?? `Zone ${b.zoneId}`,
    from: neededByToDisplay(b.from),
    to: neededByToDisplay(b.to),
    state: BLAST_STATE_TO_UI[b.state] === 'active' ? 'active' : BLAST_STATE_TO_UI[b.state] === 'cleared' ? 'done' : 'scheduled',
  }))
  const weather: IOpsWeather = {
    summary: row.weather.summary ?? 'No weather data',
    tempC: row.weather.tempC ?? 0,
    windKmh: row.weather.windKmh ?? 0,
    liftsOk: row.weather.liftsOk,
    roadsOk: row.weather.roadsOk,
  }
  return { blastWindows, weather }
}

export const mapDeliveryTicketLabel = (row: IDeliveryTicketResponse): string =>
  row.ticketNo ?? `Ticket #${row.id}`

const HANDOVER_ALERT_SEVERITY: { [k: number]: OpsAlertSeverity } = {
  1: 'critical',
  2: 'warning',
  3: 'info',
  4: 'info',
}

export const mapHandoverReport = (row: IHandoverReportResponse): IOpsHandoverReport => ({
  shiftName: row.shiftName ?? 'Shift',
  shiftDate: row.shiftDate ?? '—',
  generatedAt: formatDateForDisplay(row.generatedAt) ?? row.generatedAt,
  planDeltas: row.planDeltas.map((d) => ({
    id: d.planId,
    planName: d.planName,
    deltaThisShift: `${d.deltaThisShift >= 0 ? '+' : ''}${d.deltaThisShift} t`,
    totalProgress: `${d.totalProgress} t total`,
    progressPercent: d.progressPercent == null ? null : Math.round(d.progressPercent),
  })),
  tasksPlanned: row.tasksPlanned,
  tasksCompleted: row.tasksCompleted,
  cyclesCompleted: row.cyclesCompleted,
  incompleteTasks: [],
  openReactiveTasks: [],
  machinesDown: row.machinesDown.map((m) => ({
    id: m.machineId,
    name: m.name,
    since: '—',
    repairStatus: m.repairStatus,
    estimatedReturn: '—',
  })),
  alertsBySeverity: row.alertsBySeverity.map((a) => ({
    severity: HANDOVER_ALERT_SEVERITY[a.severity] ?? 'info',
    count: a.count,
  })),
  workerUtilizationPct: 0,
  machineUtilizationPct: 0,
  idleTimePct: 0,
  nextShiftRisks: row.nextShiftRisks,
})
